import React from 'react';
import { CheckCircle, Loader, Circle, AlertTriangle } from 'lucide-react';

export interface RemediationStep {
  id: string;
  label: string;
  status: 'pending' | 'in-progress' | 'complete' | 'error';
  detail?: string;
}

interface RemediationProgressProps {
  steps: RemediationStep[];
  isActive: boolean;
}

const RemediationProgress: React.FC<RemediationProgressProps> = ({ steps, isActive }) => {
  if (!isActive && steps.every(s => s.status === 'pending')) {
    return null;
  }

  const completedCount = steps.filter(s => s.status === 'complete').length;
  const progressPercent = (completedCount / steps.length) * 100;

  return (
    <div className="bg-arbitium-darker/50 border border-arbitium-primary/20 rounded-xl p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-white flex items-center gap-2">
          🔄 Remediation Progress
        </h4>
        <span className="text-xs text-gray-400">
          {completedCount}/{steps.length} steps
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-arbitium-darker rounded-full h-2 mb-4">
        <div 
          className="h-2 rounded-full bg-gradient-to-r from-arbitium-primary to-arbitium-accent transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, index) => (
          <div 
            key={step.id}
            className={`flex items-center gap-3 p-2 rounded-lg transition-all duration-300 ${
              step.status === 'in-progress' 
                ? 'bg-arbitium-primary/10 border border-arbitium-primary/30' 
                : step.status === 'complete'
                ? 'bg-green-500/5'
                : step.status === 'error'
                ? 'bg-red-500/10'
                : 'opacity-50'
            }`}
          >
            {/* Status Icon */}
            <div className="w-6 h-6 flex items-center justify-center">
              {step.status === 'complete' && (
                <CheckCircle className="w-5 h-5 text-green-400" />
              )}
              {step.status === 'in-progress' && (
                <Loader className="w-5 h-5 text-arbitium-accent animate-spin" />
              )}
              {step.status === 'pending' && (
                <Circle className="w-5 h-5 text-gray-600" />
              )}
              {step.status === 'error' && (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              )}
            </div>

            {/* Step Info */}
            <div className="flex-1">
              <div className={`text-sm font-medium ${
                step.status === 'complete' ? 'text-green-400' :
                step.status === 'in-progress' ? 'text-white' :
                step.status === 'error' ? 'text-red-400' :
                'text-gray-500'
              }`}>
                {step.label}
              </div>
              {step.detail && step.status === 'in-progress' && (
                <div className="text-xs text-gray-400 mt-0.5 font-mono">
                  {step.detail}
                </div>
              )}
            </div>

            {/* Step Number */}
            <div className={`text-xs px-2 py-0.5 rounded ${
              step.status === 'complete' ? 'bg-green-500/20 text-green-400' :
              step.status === 'in-progress' ? 'bg-arbitium-primary/20 text-arbitium-accent' :
              'bg-gray-800 text-gray-500'
            }`}>
              {index + 1}
            </div>
          </div>
        ))}
      </div>

      {/* Completion Message */}
      {completedCount === steps.length && (
        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            All remediation steps completed successfully
          </div>
        </div>
      )}
    </div>
  );
};

export default RemediationProgress;
